import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { formatMoney } from "../lib/api.js";
import { currencyFromLocale } from "../../../shared/plans.js";
import { isCheckoutOfferKind, type CheckoutOfferKind } from "../../../shared/checkout_offers.js";
import type { Plan } from "../../../shared/types.js";

export function CheckoutOfferBanner({
  kind,
  plan,
  discountCents,
  firstCents,
  recurringCents,
  addOnCents = 0,
  surface = "checkout",
  className,
}: {
  kind: string;
  plan?: Plan;
  discountCents: number;
  firstCents: number;
  recurringCents: number;
  /** Paid add-ons ticked on the checkout: full price, today and every month. */
  addOnCents?: number;
  surface?: "checkout" | "plans";
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  if (!isCheckoutOfferKind(kind)) return null;
  const currency = currencyFromLocale(i18n.language);
  const money = (cents: number) => formatMoney(cents, i18n.language, currency);
  const copy = offerBannerCopy(kind, {
    t,
    money,
    plan,
    discountCents,
    firstCents,
    recurringCents,
    addOnCents,
    surface,
  });

  return (
    <aside className={`checkout-offer-banner ${className ?? ""}`} aria-label={copy.seal}>
      <div className="checkout-offer-seal">
        <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {copy.seal}
      </div>
      <p className="checkout-offer-banner-body">{copy.body}</p>
    </aside>
  );
}

function offerBannerCopy(
  kind: CheckoutOfferKind,
  args: {
    t: (key: string, opts?: Record<string, string>) => string;
    money: (cents: number) => string;
    plan?: Plan;
    discountCents: number;
    firstCents: number;
    recurringCents: number;
    addOnCents: number;
    surface: "checkout" | "plans";
  },
) {
  if (kind === "premium_at_basic") {
    return {
      seal: args.t("checkout.offerBadgePremiumAtBasic"),
      body: args.t("checkout.offerBodyPremiumAtBasic"),
    };
  }
  if (args.surface === "plans") {
    return {
      seal: args.t("checkout.offerBadgeFirstMonthPrice", { amount: args.money(args.firstCents) }),
      body: args.t("checkout.offerBodyFirstMonthPlans", { today: args.money(args.firstCents) }),
    };
  }
  // The seal names the offer's price; the body says what is charged, add-ons included.
  return {
    seal: args.t("checkout.offerBadgeFirstMonthPrice", { amount: args.money(args.firstCents) }),
    body: args.t("checkout.offerBodyFirstMonthOff", {
      today: args.money(args.firstCents + args.addOnCents),
      recurring: args.money(args.recurringCents + args.addOnCents),
      plan: args.plan ? args.t(`plans.${args.plan}`) : "",
    }),
  };
}
